import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ITrainersData,
  ITrainersPage,
  ITrainersTool,
  ITrainerSubmit,
} from '@/types/trainers';
import {
  getTrainersContent,
  submitBenchmark,
  submitTrainersResponse,
} from '@/api/qa';
import { ApiErrorEnum } from '@/types/api';
import { HEADER_HEIGHT } from '@/utils/explorer';
import { useTrainerFeedback } from '@/hooks/useTrainerFeedback';
import Feedback from '@/components/ui/trainers/shared/Feedback';
import {
  getNormalizedDetections,
  getNormalizedImages,
  getNormalizedPoints,
} from '@/utils/trainers';
import { DisplayOnMap } from '@/components/ui/trainers/shared/DisplayOnMap';
import { useTrainerReviews } from '@/hooks/useTrainerReviews';
import TrainerHeader from './TrainerHeader';
import ImageCarousel from './ImageCarousel';
import TrainerFooter from './TrainerFooter';
import TrainerController, {
  TrainerButtons,
  TrainerInputs,
  TrainerSelect,
} from './TrainerController';
import ErrorMessage from './shared/ErrorMessage';

interface Props extends ITrainersPage {
  data?: ITrainersData;
  tool?: ITrainersTool;
}

const ImageLocation: React.FC<Props> = ({
  data: initialData,
  page,
  name,
  description: initialDescription,
  location,
  tool,
}) => {
  const headerRef = useRef<HTMLDivElement>(null);
  const controllerRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const toolId = initialData?.toolId || '';
  const [data, setData] = useState<ITrainersData | undefined>(initialData);
  const { incrementReviews } = useTrainerReviews();
  const {
    leaseId,
    lat: initialLat,
    lon: initialLon,
    frames,
    closeFrames,
    images: contentImages = [],
    detections,
    detectionsByFrame,
    imagesCoords,
    positionCandidates,
  } = data?.content || {};

  const description = data?.content
    ? initialDescription ||
      `Find the outlined element in the image (at left) on the aerial map (at right) by adjusting the circle's location`
    : '';

  const hasAppleMaps = !!tool?.args?.hasAppleMaps;
  const isDataReady = Boolean(data?.content);

  const [isBenchmark, setIsBenchmark] = useState(false);
  const [contentHeight, setContentHeight] = useState(0);

  const { feedback, setFeedback } = useTrainerFeedback();

  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [imageHeight, setImageHeight] = useState(0);
  const [lat, setLat] = useState<number>(initialLat);
  const [lon, setLon] = useState<number>(initialLon);

  let mapDisplayOptions = {};

  if (imagesCoords) {
    mapDisplayOptions = {
      selectedCameraIndex: selectedImageIndex,
      alignViewBasedOnPoints: true,
    };
  }

  if (positionCandidates) {
    mapDisplayOptions = {
      hideTargetMarker: true,
      alignViewBasedOnPoints: true,
      selectedCameraIndex: undefined,
    };
  }

  const normalizedImages = useMemo(
    () => getNormalizedImages(frames, closeFrames, contentImages),
    [frames, contentImages, closeFrames],
  );

  const normalizedPoints = useMemo(
    () =>
      getNormalizedPoints(
        frames,
        closeFrames,
        imagesCoords ?? positionCandidates,
      ),
    [imagesCoords, positionCandidates, frames, closeFrames],
  );

  const normalizedDetections = useMemo(
    () =>
      getNormalizedDetections(
        frames,
        closeFrames,
        detectionsByFrame,
        detections,
      ),
    [detections, frames, detectionsByFrame, closeFrames],
  );

  const clearAllData = () => {
    setData(undefined);
    setIsBenchmark(false);
  };

  const refetchClient = useCallback(
    async (id: string) => {
      const _data: ITrainersData = await getTrainersContent(id, page);
      setData(_data);
    },
    [page],
  );

  const onBenchmarkToggle = (checked: boolean) => {
    setIsBenchmark(checked);
  };

  const onSubmit = async ({ outcome, annotations, value }: ITrainerSubmit) => {
    clearAllData();
    if (!leaseId) {
      return;
    }

    const sending = {
      leaseId,
      ...(annotations && { annotations }),
      ...(outcome && { outcome }),
      ...(value && { value }),
    };

    console.log('sending', sending);
    if (isBenchmark) {
      await submitBenchmark(toolId, sending);
    }

    const { error, benchmarkFeedback } = await submitTrainersResponse(
      toolId,
      sending,
    );

    if (benchmarkFeedback) {
      setFeedback({
        ...benchmarkFeedback,
        instructionsLink: tool?.args?.instructions,
      });
    }
    if (error) {
      setData({ error });
    } else {
      incrementReviews(toolId);
      refetchClient(toolId);
    }
  };

  useEffect(() => {
    setLat(initialLat);
    setLon(initialLon);
  }, [initialLat, initialLon]);

  useEffect(() => {
    const calculateContentHeight = () => {
      const pageHeaderHeight = HEADER_HEIGHT;
      const sectionHeaderHeight = headerRef.current?.offsetHeight ?? 0;
      const controllerHeight = controllerRef.current?.offsetHeight ?? 0;
      const footerHeight = footerRef.current?.offsetHeight ?? 0;
      const newContentHeight =
        window.innerHeight -
        pageHeaderHeight -
        sectionHeaderHeight -
        controllerHeight -
        footerHeight;
      setContentHeight(newContentHeight);
    };

    calculateContentHeight();
    window.addEventListener('resize', calculateContentHeight);

    return () => window.removeEventListener('resize', calculateContentHeight);
  }, [leaseId]);

  useEffect(() => {
    if (data && !data.error && !normalizedImages.length) {
      const error = ApiErrorEnum.NoContent;
      setData({ error });
    }
  }, [data, normalizedImages]);

  return (
    <div className="flex size-full flex-col">
      <TrainerHeader
        data={data}
        name={name}
        description={description}
        location={location}
        tool={tool}
        elementRef={headerRef}
      />
      {data?.error ? (
        <ErrorMessage error={data.error} />
      ) : (
        <>
          <div
            className="relative flex grow items-center overflow-hidden"
            style={{ height: contentHeight }}>
            <div className="mt-2 flex w-full">
              {imageHeight && leaseId && lat && lon ? (
                <div
                  className="relative h-full w-1/2"
                  style={{ height: imageHeight }}>
                  <DisplayOnMap
                    target={{
                      lat,
                      lon,
                    }}
                    points={normalizedPoints}
                    options={{
                      hasAppleMaps,
                      ...mapDisplayOptions,
                    }}
                  />
                </div>
              ) : null}
              <div className="relative h-full w-1/2">
                <ImageCarousel
                  key={data?.content?.leaseId}
                  images={normalizedImages}
                  detections={normalizedDetections}
                  onImageLoaded={setImageHeight}
                  onSelectedImageChange={setSelectedImageIndex}
                  startWithIndex="largestDetection"
                  useNumberIndicator={true}
                />
              </div>
            </div>
          </div>
          <TrainerController
            containerRef={controllerRef}
            isDataReady={isDataReady}
            onSubmit={onSubmit}
            data={data}
            tool={tool}>
            <TrainerButtons />
            <TrainerSelect />
            <TrainerInputs />
          </TrainerController>
        </>
      )}
      <TrainerFooter
        data={data}
        elementRef={footerRef}
        onBenchmarkToggle={onBenchmarkToggle}
        isBenchmark={isBenchmark}
      />
      {feedback?.feedbackMessage && <Feedback />}
    </div>
  );
};

export default ImageLocation;
